import React, { useState, useEffect, useRef } from 'react';
import isURL from 'isurl';
import 'froala-editor/js/froala_editor.pkgd.min';
import 'froala-editor/js/froala_editor.min';
import 'froala-editor/js/plugins/align.min';
import 'froala-editor/js/plugins/char_counter.min';
import 'froala-editor/js/plugins/code_beautifier.min';
import 'froala-editor/js/plugins/code_view.min';
import 'froala-editor/js/plugins/colors.min';
import 'froala-editor/js/plugins/emoticons.min';
import 'froala-editor/js/plugins/font_family.min';
import 'froala-editor/js/plugins/font_size.min';
import 'froala-editor/js/plugins/fullscreen.min';
import 'froala-editor/js/plugins/image.min';
import 'froala-editor/js/plugins/image_manager.min';
import 'froala-editor/js/plugins/paragraph_format.min';
import 'froala-editor/js/plugins/paragraph_style.min';
import 'froala-editor/js/plugins/table.min';
import 'froala-editor/js/plugins/link.min';
import 'froala-editor/js/plugins/lists.min';
import FroalaEditor from 'react-froala-wysiwyg';

import './styles/SAFroala.scss';
import Spinner from 'Legacy/components/spinner/spinner';
import FA from 'utils/FA';
import Routes from '../../../routes';

const TOOLBAR_BUTTONS = [
  'paragraphFormat',
  'bold',
  'italic',
  'underline',
  'strikeThrough',
  'fontFamily',
  'fontSize',
  'textColor',
  'emoticons',
  'clearFormatting',
  '-',
  'align',
  'formatOL',
  'formatUL',
  'indent',
  'outdent',
  '|',
  'insertImage',
  'insertLink',
  'insertTable',
  'insertHR',
  '|',
  'html'
];

const SIMPLIFIED_TOOLBAR = [
  'paragraphFormat',
  'bold',
  'italic',
  'underline',
  'strikeThrough',
  'fontFamily',
  'fontSize',
  'textColor',
  'clearFormatting',
  'insertImage',
  'insertLink',
  'html'

];


const IMAGE_EDIT_BUTTONS = [
  'imageAlign',
  'imageRemove',
  '|',
  'imageLink',
  'linkOpen',
  'linkEdit',
  'linkRemove',
  '-',
  'imageDisplay',
  'imageStyle',
  'imageAlt',
  'imageSize'
];

const FONTS = {
  "'Helvetica Neue'sans-serif": 'Helvetica Neue',
  'Helvetica,sans-serif': 'Helvetica',
  'Arial,Helvetica,sans-serif': 'Arial',
  'Georgia,serif': 'Georgia',
  'Impact,Charcoal,sans-serif': 'Impact',
  'Tahoma,Geneva,sans-serif': 'Tahoma',
  "'Times New Roman',Times,serif": 'Times New Roman',
  'Verdana,Geneva,sans-serif': 'Verdana',
  Arvo: 'Arvo',
  'EB Garamond': 'EB Garamond',
  Lato: 'Lato',
  'Libre Baskerville,serif': 'Libre Baskerville',
  'Lora,serif': 'Lora',
  'Noto Sans,sans-serif': 'Noto Sans',
  'Open Sans,sans-serif': 'Open Sans',
  'Oswald,sans-serif': 'Oswald',
  'PT Serif,serif': 'PT Serif',
  'Roboto,sans-serif': 'Roboto',
  'Pinyon Script,cursive': 'Pinyon Script',
  'Tangerine,cursive': 'Tangerine'
};

// Overrides original froala sanitizeUrl method to allow merge tokens as urls (i.e. {{recipient.verification_link}})
function sanitizeUrl(url) {
  if (/^{{.*}}$/i.test(url)) {
    return url;
  }

  if (/^(https?:|ftps?:|)\/\//i.test(url)) {
    if (!isURL(url) && !isURL(`http:${url}`)) {
      return '';
    }
  }

  return encodeURIComponent(url)
    .replace(/%23/g, '#')
    .replace(/%2F/g, '/')
    .replace(/%25/g, '%')
    .replace(/mailto%3A/gi, 'mailto:')
    .replace(/file%3A/gi, 'file:')
    .replace(/sms%3A/gi, 'sms:')
    .replace(/tel%3A/gi, 'tel:')
    .replace(/notes%3A/gi, 'notes:')
    .replace(/data%3Aimage/gi, 'data:image')
    .replace(/blob%3A/gi, 'blob:')
    .replace(/webkit-fake-url%3A/gi, 'webkit-fake-url:')
    .replace(/%3F/g, '?')
    .replace(/%3D/g, '=')
    .replace(/%26/g, '&')
    .replace(/&amp;/g, '&')
    .replace(/%2C/g, ',')
    .replace(/%3B/g, ';')
    .replace(/%2B/g, '+')
    .replace(/%40/g, '@')
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']')
    .replace(/%7B/g, '{')
    .replace(/%7D/g, '}');
}


const SAFroala = (props) => {
    const [codeView, setCodeView] = useState(false);
    const [toolbarShown, setToolbarShown] = useState(!props.hideToolbar);
    const editorRef = useRef(null);
    const [initialized, setInitialized] = useState(false);

    const { value, htmlAllowDangerousTags, showFormatting, simplifiedFormatting, config, className, froalaOptions, hideToolbar } = props;

    let toolbarButtons = [];
    if (showFormatting) {
      toolbarButtons = simplifiedFormatting ? SIMPLIFIED_TOOLBAR : TOOLBAR_BUTTONS
    }

    const htmlRemoveTags = [];
    if (!htmlAllowDangerousTags) {
      htmlRemoveTags.push('script');
    }

    return (
      <div>
        {(hideToolbar && toolbarShown) &&
          <span className='toolbar-expand'>
            <button
              className='btn btn-primary btn-xs'
              onClick={() => {
                editorRef.current.editor.toolbar.hide();
                setToolbarShown(false);
              }}
            >
              Hide Toolbar
              <FA name='chevron-down' nbsp />
            </button>
          </span>}
        {(hideToolbar && !toolbarShown) &&
          <span className='toolbar-expand'>
            <button
              className='btn btn-primary btn-xs'
              onClick={() => {
                editorRef.current.editor.toolbar.show();
                setToolbarShown(true);
              }}
            >
              Show Toolbar
              <FA name='chevron-right' nbsp />
            </button>
          </span>}

        <div className={className || 'sa-froala'}>
          <FroalaEditor
            {...props}
            model={initialized ? value : undefined}
            config={Object.assign({
              charCounterCount: false,
              events: {
                'initialized': function () {
                  setInitialized(true);
                  if (hideToolbar) {
                    editorRef.current.editor.toolbar.hide();
                  }
                },
                'helpers.sanitizeURL': sanitizeUrl,
                'commands.after': () => {
                  setCodeView(editorRef.current.editor.codeView.isActive());
                }
              },
              ...froalaOptions,
              toolbarButtons,
              toolbarButtonsMD: toolbarButtons,
              toolbarButtonsSM: toolbarButtons,
              toolbarButtonsXS: toolbarButtons,
              // Ideally we want toolbarSticky to be true as it makes the editor much more usable for large content.
              // However it causes display issues per SA-20302. If we upgrade Froala we should enable it to see
              // if the issue is resoled.
              toolbarSticky: false,
              imageEditButtons: IMAGE_EDIT_BUTTONS,
              imageManagerLoadURL: Routes.api_image_resources_path(),
              requestHeaders: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content') },
              imageResizeWithPercent: true,
              imageDefaultWidth: 0,
              imageManagerPageSize: 20,
              imageManagerPreloader: Spinner.IMAGE_URL,
              imageManagerDeleteURL: Routes.api_image_resources_path(),
              imageManagerDeleteMethod: 'DELETE',
              imageManagerDeleteParams: { authenticity_token: $('meta[name=csrf-token]').attr('content') },
              imageUploadParam: 'image_resource[file]',
              imageUploadURL: Routes.api_image_resources_path(),
              htmlRemoveTags,
              fontSize: ['8', '9', '10', '11', '12', '13', '14', '16', '18', '24', '30', '36', '48', '60', '72', '96'],
              fontFamily: FONTS,
              key: 'Lc2C1qE1E2E4D4B3B14A9iAd1Te1YZNYAc1CUKUEQOHFVANUqB2C1E7E6E1F5G4A1E3C7==',
              tableStyles: {
                'fr-no-borders': 'No Borders',
                'fr-dashed-borders': 'Dashed Borders',
                'fr-alternate-rows': 'Alternate Rows'
              },
            }, config)}
            ref={editorRef}
          />


          {codeView &&
            (
              <div className='sa-froala-code-view-alert alert'>
                <FA name='warning' nbsp />
                Changes in code view will not be applied until you preview them.

                <button
                  className='btn btn-xs btn-success'
                  onClick={() => {
                    editorRef.current.editor.codeView.toggle();
                    setCodeView(false);
                    return false;
                  }}
                >
                  Preview Changes
                </button>
              </div>
            )
          }
        </div>
      </div>
    );
};


export default SAFroala;
